import { useState, useEffect, useCallback, useRef } from 'react';
import { useData } from '../../contexts/DataContext';

/**
 * useCourseProgress - Hook to fetch and compute events + assignments progress
 * @param {Object} course - Course object with id/endeavorId
 * @returns {Object} { eventsPercent, assignmentsPercent, loading, error }
 */
// Global debounce map to prevent thundering herd when many courses mount simultaneously
const fetchDebounceMap = new Map();
const DEBOUNCE_DELAY = 50; // 50ms delay between course fetches

export function useCourseProgress(course) {
  const { getFilteredEvents, getFilteredAssignments } = useData();
  const [eventsPercent, setEventsPercent] = useState(0);
  const [assignmentsPercent, setAssignmentsPercent] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  // Use ref to prevent flashing when events shouldn't change
  const eventsPercentRef = useRef(0);
  const assignmentsPercentRef = useRef(0);
  const mountIndexRef = useRef(0);
  const hasInitiallyLoadedRef = useRef(false);

  const courseId = course?.id || course?.endeavorId || course?.courseId;
  
  // Assign a mount index to stagger fetches
  useEffect(() => {
    if (!mountIndexRef.current) {
      const currentCount = fetchDebounceMap.size;
      mountIndexRef.current = currentCount + 1;
      fetchDebounceMap.set(courseId, true);
      
      // Cleanup on unmount
      return () => {
        fetchDebounceMap.delete(courseId);
      };
    }
  }, [courseId]);

  const fetchProgressData = useCallback(async () => {
    if (!courseId) {
      setEventsPercent(0);
      setAssignmentsPercent(0);
      return;
    }

    // Only show loading spinner on initial fetch, not on refresh triggers
    // This prevents progress bars from flashing to 0% when dataRefreshTrigger changes
    if (!hasInitiallyLoadedRef.current) {
      setLoading(true);
    }
    setError(null);

    try {
      // Stagger fetches to prevent thundering herd (20 courses mounting simultaneously)
      const delayMs = (mountIndexRef.current || 0) * DEBOUNCE_DELAY;
      if (delayMs > 0) {
        await new Promise(resolve => setTimeout(resolve, delayMs));
      }
      
      // Read from hydrated cache (no API calls) and filter client-side by endeavorId
      const [eventsData, assignmentsData] = await Promise.all([
        getFilteredEvents({ 
          endeavorId: courseId
        }).catch(() => []),
        getFilteredAssignments({ 
          endeavorId: courseId
        }).catch(() => [])
      ]);

      // Compute events progress (past events / total events)
      const events = eventsData || [];
      const totalEvents = events.length;
      
      let newEventsPercent = 0;
      if (totalEvents === 0) {
        newEventsPercent = 0;
      } else {
        const now = new Date();
        const completedEvents = events.filter(event => {
          const eventDate = new Date(
            event.startTime || 
            event.startDate || 
            event.startDatetime || 
            event.startDateTime || 
            event.eventDate || 
            event.date || 
            event.start || 
            event.begin
          );
          return eventDate < now;
        }).length;
        
        newEventsPercent = Math.round((completedEvents / totalEvents) * 100);
      }
      
      // Only update if changed to prevent unnecessary re-renders
      if (eventsPercentRef.current !== newEventsPercent) {
        eventsPercentRef.current = newEventsPercent;
        setEventsPercent(newEventsPercent);
      }

      // Compute assignments progress
      const assignments = Array.isArray(assignmentsData) ? assignmentsData : (assignmentsData?.content || []);
      const totalAssignments = assignments.length;
      
      let newAssignmentsPercent = 0;
      if (totalAssignments === 0) {
        newAssignmentsPercent = 0;
      } else {
        const completedAssignments = assignments.filter(assignment => {
          return assignment.completed === true || 
                 assignment.status === 'completed' || 
                 assignment.status === 'Completed' ||
                 assignment.submissionStatus === 'Completed';
        }).length;
        
        newAssignmentsPercent = Math.round((completedAssignments / totalAssignments) * 100);
      }
      
      // Only update if changed to prevent unnecessary re-renders
      if (assignmentsPercentRef.current !== newAssignmentsPercent) {
        assignmentsPercentRef.current = newAssignmentsPercent;
        setAssignmentsPercent(newAssignmentsPercent);
      }

    } catch (err) {
      console.error('Error fetching course progress data:', err);
      setError(err.message || 'Failed to fetch progress data');
      setEventsPercent(0);
      setAssignmentsPercent(0);
    } finally {
      setLoading(false);
      hasInitiallyLoadedRef.current = true;
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [courseId, getFilteredEvents, getFilteredAssignments]);

  // Fetch on mount, when course changes, or when dataRefreshTrigger changes
  useEffect(() => {
    fetchProgressData();
  }, [fetchProgressData]);

  return {
    eventsPercent,
    assignmentsPercent,
    loading,
    error,
    refetch: fetchProgressData
  };
}